// PostHog analytics integration for Receipt Chaser.
//
// Design constraints (from product decisions, not arbitrary):
//   1. EU-hosted (eu.i.posthog.com) so analytics data stays in the EU,
//      consistent with the rest of the data model.
//   2. Marketing routes ONLY. Never loads on authenticated app pages
//      (dashboards, request detail, settings, admin, support) or the
//      unauthenticated /request/<token> client portal where users
//      upload sensitive financial documents.
//   3. Gated behind CookieYes analytics consent. Until the user accepts
//      the 'analytics' cookie category, posthog.init() is not called
//      and no events fire.
//   4. Singleton init: posthog.init() must only be called once per page
//      load. We track this with a module-level flag.
//   5. Custom events are signup-funnel-relevant only (signup_completed
//      to start; more can be added). No general click tracking, no
//      autocapture, no session recording.
//
// Failure modes are all silent: missing env var, missing consent, missing
// CookieYes, refused consent -- in every case, PostHog simply doesn't
// load. No errors to the user, no console spam.

import posthog from 'posthog-js'

const POSTHOG_KEY  = process.env.REACT_APP_POSTHOG_KEY
const POSTHOG_HOST = 'https://eu.i.posthog.com'

// True once posthog.init() has succeeded this page load. Guards against
// double-init if consent changes multiple times or the user navigates
// between marketing routes after consenting.
let initialised = false

// Whitelist of public marketing routes that may load PostHog.
// IMPORTANT: This is a whitelist, not a blacklist. Adding a new
// route below opts that route IN to tracking. Adding a new sensitive
// route elsewhere in the app does nothing here -- it stays untracked
// by default, which is the safe direction.
//
// Notable exclusions and why:
//   /request/<token>   -- unauthenticated client portal where clients
//                          upload financial receipts. Third-party JS
//                          must not touch this surface.
//   /join              -- team-member invitation acceptance. Not a
//                          marketing conversion path.
//   /dashboard, /clients/*, /requests/*, /settings, /admin, /support/*
//                       -- authenticated app surface, not marketing.
const MARKETING_PATHS = new Set([
  '/',
  '/features',
  '/how-it-works',
  '/pricing',
  '/contact',
  '/faq',
  '/terms',
  '/privacy',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/articles',
])

// Article post pages match /articles/<slug>. We can't list every slug
// in the Set above because slugs are unbounded, so prefix-match these
// separately. This is still a whitelist: only paths under /articles/
// are allowed, and only at a single sub-level (slugs cannot contain
// slashes).
function isArticlePostRoute(pathname) {
  if (!pathname.startsWith('/articles/')) return false
  const rest = pathname.slice('/articles/'.length)
  // Must be a single segment with no further slashes -- protects against
  // anything weird like /articles/x/y or /articles/x/admin
  return rest.length > 0 && !rest.includes('/')
}

export function isMarketingRoute(pathname) {
  return MARKETING_PATHS.has(pathname) || isArticlePostRoute(pathname)
}

// Reads the CookieYes consent state and returns true iff the user
// has accepted the 'analytics' cookie category.
//
// CookieYes exposes two surfaces:
//   - window.getCkyConsent()  -- function returning current consent
//   - document event 'cookieyes_consent_update' -- fires on change
//
// If CookieYes hasn't loaded yet (race on first paint), this returns
// false. The caller is expected to subscribe to the update event and
// retry when consent state changes.
function hasAnalyticsConsent() {
  if (typeof window === 'undefined') return false
  if (typeof window.getCkyConsent !== 'function') return false
  try {
    const consent = window.getCkyConsent()
    // CookieYes exposes a `categories` object with boolean flags per
    // category. Older versions used `accepted` arrays. Check both
    // shapes to be safe against version drift.
    if (consent?.categories?.analytics === true) return true
    if (Array.isArray(consent?.accepted) && consent.accepted.includes('analytics')) return true
    return false
  } catch {
    return false
  }
}

// Initialise PostHog if everything's in order. Safe to call multiple
// times -- subsequent calls are no-ops after the first success.
//
// Conditions for actually initing:
//   1. We're in a browser (not SSR / during build)
//   2. The API key env var is set
//   3. Analytics consent has been granted
//
// Returns true if initialised this call or previously; false if we
// declined to init (caller can subscribe to consent changes and retry).
export function tryInitPostHog() {
  if (initialised) return true
  if (typeof window === 'undefined') return false
  if (!POSTHOG_KEY) return false
  if (!hasAnalyticsConsent()) return false

  posthog.init(POSTHOG_KEY, {
    api_host:        POSTHOG_HOST,
    defaults:        '2026-01-30',
    person_profiles: 'identified_only',
    // Belt and braces: even though we only call init on marketing
    // routes, this disables PostHog's default behaviour of capturing
    // pageviews / pageleaves automatically. We fire pageviews ourselves
    // from PostHogTracker so we can apply the route whitelist on every
    // navigation, not just initial load.
    capture_pageview: false,
    capture_pageleave: false,
    // Don't capture exceptions automatically -- we don't want PostHog
    // surfacing internal error data alongside marketing analytics.
    capture_exceptions: false,
    // Don't send heatmap data (we agreed: no autocapture, no recordings).
    enable_heatmaps: false,
    // Disable in development so localhost noise doesn't pollute prod stats.
    // Toggle this off if you actually want to test PostHog locally.
    loaded: (ph) => {
      if (process.env.NODE_ENV === 'development') {
        ph.opt_out_capturing()
      }
    },
  })

  initialised = true
  return true
}

// Capture a pageview manually. Used by PostHogTracker on every route
// change. Returns silently if PostHog isn't initialised (e.g. no consent).
export function capturePageview(pathname) {
  if (!initialised) return
  posthog.capture('$pageview', {
    $current_url: window.location.origin + pathname,
    path:         pathname,
  })
}

// Capture a custom event. Used for funnel events like signup_completed.
// Returns silently if PostHog isn't initialised.
//
// Example: trackEvent('signup_completed', { method: 'email' })
export function trackEvent(eventName, properties) {
  if (!initialised) return
  posthog.capture(eventName, properties || {})
}

// Listen for CookieYes consent changes. If the user accepts analytics
// AFTER initial page load, we want to init PostHog at that point.
//
// Call this once at app boot. Safe to call multiple times -- only
// installs the listener on the first call.
let consentListenerInstalled = false
export function watchForConsent() {
  if (consentListenerInstalled) return
  if (typeof document === 'undefined') return
  consentListenerInstalled = true
  document.addEventListener('cookieyes_consent_update', () => {
    tryInitPostHog()
  })
}
